import {DatePipe} from '@angular/common';
import {Component, OnDestroy, OnInit, ViewEncapsulation} from "@angular/core";
import {FormBuilder, Validators} from "@angular/forms";
import {Response} from "@angular/http";
import {MdDialog, MdDialogConfig, MdDialogRef} from "@angular/material";
import {ActivatedRoute, Router} from "@angular/router";
import {Subscription} from "rxjs";
import {APIService, User} from "../../services/login/api";
import {ConfirmEmailSentDialogComponent} from "./confirm-email-sent-dialog/confirm-email-sent-dialog.component";
import {ForgotPasswordDialogComponent} from "./forgot-password-dialog/forgot-password-dialog.component";


@Component({
  selector: "login-screen",
  template: require("./login.component.html"),
  styles: [require("./login.component.scss")],
  encapsulation: ViewEncapsulation.None,
  providers: [DatePipe]
})
export class LoginComponent implements OnInit, OnDestroy {
  public isLoading: boolean = false;
  public isLoggedIn: boolean = false;
  public errorMessage: string;
  public returnTo: string;
  public afterHash: string;
  public resetPasswordUrl: string = "/reset-password";
  public isLastLogin: boolean = false;
  public previousLogin: string = '';

  public DATE_FORMAT = 'y MMM d hh:mm';


  private _queryParamsSubscription: Subscription;
  private _fragmentSubscription: Subscription;

  public loginForm;
  public lockedUntil;


  private ssoValidationRegx  = new RegExp(
    `(localhost|backoffice|backoffice-demo|jordi|primor|insight|app|evaluation|kibana|portal|nmi-admin|change-all|inspector-ui|solex|concurtax|cockpit|spinnach|rails|dynoconfig|bluedoc)`
  );

  constructor(
    private apiService: APIService,
    private route: ActivatedRoute,
    private router: Router,
    private _dialog: MdDialog,
    public fb: FormBuilder,
    private datePipe: DatePipe
  ) {
  }

  public ngOnInit(): void {
    // Hack made by someone who want to test the sso
    let ssoHostName = document.location.hostname;
    let ssoParams = !!document.location.search
      ? `${document.location.search}&`
      : `?`;
    let ssoRedirectPath = this.getSsoRedirectPath(ssoHostName, ssoParams);
    if (!!ssoRedirectPath) document.location.replace(ssoRedirectPath);

    // init login form
    this.loginForm = this.fb.group({
      email: ["", Validators.email],
      password: [
        "",
        Validators.compose([Validators.required, Validators.minLength(3)])
      ]
    });

    this._queryParamsSubscription = this.route.queryParams.subscribe(
      queryParams => {
        if (queryParams && queryParams["returnTo"]) {
          this.returnTo = decodeURIComponent(queryParams["returnTo"]);
          if (this.returnTo.toLocaleLowerCase().includes('javascript')) this.returnTo = undefined;
          else if (this.returnTo.toLocaleLowerCase().includes('http')) this.returnTo = undefined;
        }
      }
    );

    this._fragmentSubscription = this.route.fragment.subscribe(fragment => {
      this.afterHash = fragment;
    });
  }

  public ngOnDestroy(): void {
    if (typeof this._queryParamsSubscription !== "undefined")
      this._queryParamsSubscription.unsubscribe();

    if (typeof this._fragmentSubscription !== "undefined")
      this._fragmentSubscription.unsubscribe();
  }

  //Redirect function
  redirect(retTo, browserWindow) {
    if (typeof this.returnTo !== "undefined") {
      let redirectingTo = this.returnTo + (this.afterHash ? `#${this.afterHash}` : "");
      browserWindow.location.assign(redirectingTo);
    } else {
      document.location.href = "/";
    }
  }

  onSubmit(
    callback?: (error?: any) => void,
    browserWindow: any = window
  ): boolean {
    delete this.errorMessage;
    this.isLoading = true;
    this.apiService
      .login(this.loginForm.value.email, this.loginForm.value.password)
      .subscribe(
        (res) => {
          localStorage.setItem('isFirstTime', 'false');
          this.isLoading = false;
          if (res.status === 200) {
            this.isLoggedIn = true;
            if (res._body) {
              const userToken = res._body.user_token;
              localStorage.setItem('x-vatbox-token', userToken);
            }
            //Get user details - show last login time
            this.apiService.getUserDetails()
              .subscribe((user: User) => {
                //Show last login time
                this.previousLogin = user.previousLogin;
                this.isLastLogin = !!this.previousLogin;
                //redirect to the returnTo url
                setTimeout(() => {
                  this.redirect(this.returnTo, browserWindow);
                }, 1000);
              }, (/*error*/) => {
                this.redirect(this.returnTo, browserWindow);

              });
          } else {
            this.isLoggedIn = false;
          }
          if (callback) callback();
        },
        error => {
          if (error.status == 401) {
            switch (error.json().reason) {
              case 'LoginWithExpiredPassword' :
                this.redirectToResetPassword();
                this.errorMessage = "Password Expired";
                break;
              case 'TooManyFailedLogins':
                this.lockedUntil = error.json().lockedUntil;
                this.errorMessage = `Due to multiple failed login attempts, your account has been locked until  ${this.transformDate(this.lockedUntil)}`;
                break;
              case 'UserLockedFailure':
                this.lockedUntil = error.json().lockedUntil;
                this.errorMessage = `Account is locked until ${this.transformDate(this.lockedUntil)}`;
                break;
              case 'AuthenticationFailure':
                this.errorMessage = error.json().prettyReason;
                break;
              case 'FatladyException':
                this.errorMessage = 'Failed to login';
                break;
              case 'UserNotExistOrDeactivated':
                this.errorMessage = error.json().prettyReason;
                break;
              default:
                this.errorMessage = error.json().statusMessage;
                this.redirect('/login/', browserWindow);
                break;
            }
            this.isLoading = false;
            this.isLoggedIn = false;
            if (callback) callback(error);
          }
          if (error.status === 500) {
            this.isLoading = false;
            this.isLoggedIn = false;
            this.errorMessage = 'Something went wrong';
          }
        }
      );
    //return false to prevent default form submit behavior to refresh the page.
    return false;
  }

  public getSsoRedirectPath(ssoHostName: string, ssoParams: string): string {
    let ssoPath: string;
    let ssoMatch: RegExpMatchArray = this.ssoValidationRegx.exec(ssoHostName);
    if (ssoMatch === null) {
      let ssoClient = ssoHostName.split(".")[0];
      ssoPath = `/saml2/redirect${ssoParams}idpid=${ssoClient}`;
    }
    return ssoPath;
  }

  private redirectToResetPassword(): void {
    this.router.navigate([
      `${this.resetPasswordUrl}`,
      {email: this.loginForm.value.email}
    ]);
  }

  public forgotPasswordDialog() {
    let forgotPasswordDialogRef: MdDialogRef<ForgotPasswordDialogComponent> = this._dialog.open(ForgotPasswordDialogComponent, <MdDialogConfig>{
      disableClose: true
    });
    forgotPasswordDialogRef.afterClosed().subscribe((isSaved: boolean) => {
      if (isSaved) {
        let email: string =
          forgotPasswordDialogRef.componentInstance.forgotForm.value.email;
        this.apiService.renewPassword(email).subscribe((res: Response) => {
          if (res.status === 200) this.openConfirmDialog(email);
        }); //no error callback for security reason
      }
    });
  }

  private openConfirmDialog(mail: string) {
    let confirmEmailSentDialogRef: MdDialogRef<ConfirmEmailSentDialogComponent> = this._dialog.open(ConfirmEmailSentDialogComponent, <MdDialogConfig>{
      disableClose: false
    });
    confirmEmailSentDialogRef.componentInstance.mail = mail;
  }

  transformDate(date) {
    return this.datePipe.transform(date, this.DATE_FORMAT);
  }
}




// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/components/login/login.component.ts